import React, { useEffect, useRef } from "react";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import "dayjs/locale/fr";

// Associe les codes aux couleurs
const colorMapping = {
  0: "#DFFFF9",
  1: "#D4EA62",
  2: "#F4C842",
  3: "#F0907F",
};

const CalendarPrevision = ({ data, today }) => {
  const calendarRef = useRef(null); // Référence à la div contenant le calendrier

  useEffect(() => {
    // Après que la page soit rendue, on définit la position du scroll vers le bas
    if (calendarRef.current) {
      calendarRef.current.scrollTop = calendarRef.current.scrollHeight;
    }
  }, [data]);

  // Récupérer le mois actuel
  const currentMonth = dayjs().format("YYYY-MM");

  // Regroupe les données par mois
  const groupedData = data.reduce((acc, [date, code]) => {
    const month = date.slice(0, 7); // Ex: "2025-01"
    if (!acc[month]) acc[month] = [];
    acc[month].push({ date, code });
    return acc;
  }, {});

  // Filtrer pour ne garder que le mois actuel et les semaines suivantes
  const filteredMonths = Object.keys(groupedData).filter((month) => month >= currentMonth);

  return (
    <div
      ref={calendarRef} // Ajout de la référence à la div
      style={{
        maxHeight: "1250px", 
        overflowY: "auto", 
        display: "flex", 
        flexDirection: "column-reverse", // Cela inverse l'ordre du contenu
      }}
    >
      <div
        style={{
          display: "grid",
          gap: "20px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {filteredMonths.map((month) => {
          const dates = groupedData[month] || [];
          const lastDateInMonth = dates.length > 0 ? dates[dates.length - 1].date : null;
          const calendarRange = lastDateInMonth ? [`${month}-01`, lastDateInMonth] : month;
          const totalDays = lastDateInMonth ? dayjs(lastDateInMonth).date() : 30;
          const weeksNeeded = Math.ceil(totalDays / 7);
          const calendarHeight = weeksNeeded * 110 + 140;

          const formattedData = dates.map(({ date, code }) => ({
            value: [date, 1],
            itemStyle: {
              color: colorMapping[code],
              borderWidth: date === today ? 5 : 1,
              borderColor: date === today ? "#000000" : "#ccc",
              z: date === today ? 10 : 1,
            },
          }));

          const scatterData = dates.map(({ date }) => ({
            value: [date, 1],
            label: {
              show: true,
              formatter: () => dayjs(date).format("D"),
              color: date === today ? "#C61616" : "#000",
              fontSize: date === today ? 55 : 40,
              fontWeight: date === today ? "bold" : "normal",
            },
          }));

          const option = {
            title: {
              text: `${dayjs(month).locale("fr").format("MMMM YYYY")}`,
              left: "center",
              top: 0,
              textStyle: { fontSize: 40, fontWeight: "bold", color: "#C61616" },
            },
            visualMap: { show: false },
            calendar: {
              top: 150,
              left: "center",
              cellSize: ["auto", 90],
              monthLabel: { show: false },
              yearLabel: { show: false },
              range: calendarRange,
              orient: "vertical",
              itemStyle: { borderWidth: 1 },
              dayLabel: {
                firstDay: 1,
                nameMap: ["D", "L", "M", "M", "J", "V", "S"],
                textStyle: { fontSize: 35 },
              },
            },
            series: [
              { type: "heatmap", coordinateSystem: "calendar", data: formattedData },
              { type: "scatter", coordinateSystem: "calendar", symbolSize: 1, data: scatterData },
            ],
          };

          return (
            <div
              key={month}
              style={{
                width: "90%",
                marginBottom: "20px",
                marginLeft: "5%",
                marginRight: "5%",
              }}
            >
              <ReactECharts option={option} style={{ height: calendarHeight }} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarPrevision;
